/**
 * AdminPushSimulator — small admin tool to fire real operational push events
 * to any user_id, validating the full notification_router → push_service chain.
 *
 * Usage: paste a target user_id, pick an event, click Invia.
 * Recommended workflow:
 *   1) Su un device target (Chrome desktop o iPhone PWA) attivare le push e ottenere user_id (es. dal Profilo o dalla URL del proprio account).
 *   2) Sull'Admin: incollare user_id, scegliere "order_driver_assigned", inviare.
 *   3) Sul device target: chiudere/mettere in background DapNow → la notifica deve comparire come notifica di sistema entro 1–3 s.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Zap, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const EVENTS = [
  { value: "order_driver_assigned", label: "Driver assegnato (cliente)" },
  { value: "order_driver_nearby",   label: "Driver in arrivo (cliente)" },
  { value: "order_picked_up",       label: "Pacco ritirato (cliente)" },
  { value: "order_delivering",      label: "In consegna (cliente)" },
  { value: "order_delivered",       label: "Consegnato (cliente)" },
  { value: "order_cancelled_operational", label: "Ordine annullato (cliente)" },
  { value: "new_matching_order",    label: "Nuovo ordine compatibile (driver)" },
  { value: "driver_verified",       label: "Driver verificato (driver)" },
  { value: "driver_compliance_expiring", label: "Documento in scadenza (driver)" },
];

export default function AdminPushSimulator() {
  const [userId, setUserId] = useState("");
  const [eventType, setEventType] = useState(EVENTS[0].value);
  const [phase, setPhase] = useState("pickup");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const onSend = async () => {
    if (!userId.trim()) {
      toast.error("Inserisci un user_id target");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/push/simulate-event", {
        user_id: userId.trim(),
        event_type: eventType,
        phase: eventType === "order_driver_nearby" ? phase : undefined,
      });
      setLastResult(data);
      const pushRes = data?.results?.push;
      if (pushRes?.sent > 0) {
        toast.success(`Push inviata a ${pushRes.sent} device. Controlla il centro notifiche!`);
      } else if (pushRes?.status === "no_subscriptions") {
        toast.warning("Nessun device iscritto per quell'utente. Prima attiva le push sul device target.");
      } else {
        toast.error(`Push: ${pushRes?.status || "errore"}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setLastResult({ error: formatApiError(e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="admin-push-simulator"
      className="rounded-2xl border border-dapnow-line bg-white p-5"
    >
      <div className="flex items-center gap-2">
        <Zap className="size-5 text-dapnow-blue" />
        <h3 className="font-display text-lg font-bold tracking-tight">
          Simula evento push reale
        </h3>
      </div>
      <p className="mt-1 text-sm text-dapnow-muted">
        Triggera un evento operativo (driver_assigned, ritirato, consegnato...) attraverso
        l'intero pipeline notification_router → push_service. Usalo per verificare che le
        notifiche arrivino come notifiche di sistema anche quando l'utente target ha
        DapNow chiusa o in background.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="block font-medium text-dapnow-ink mb-1">user_id target</span>
          <input
            data-testid="admin-push-sim-user-id"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="es. fe152ba3-..."
            className="w-full h-10 px-3 rounded-xl border border-dapnow-line bg-white text-sm focus:outline-none focus:ring-2 focus:ring-dapnow-blue/40"
          />
        </label>
        <label className="text-sm">
          <span className="block font-medium text-dapnow-ink mb-1">Evento</span>
          <select
            data-testid="admin-push-sim-event"
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="w-full h-10 px-3 rounded-xl border border-dapnow-line bg-white text-sm"
          >
            {EVENTS.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </label>
        {eventType === "order_driver_nearby" && (
          <label className="text-sm sm:col-span-2">
            <span className="block font-medium text-dapnow-ink mb-1">Fase</span>
            <select
              value={phase}
              onChange={(e) => setPhase(e.target.value)}
              className="w-full h-10 px-3 rounded-xl border border-dapnow-line bg-white text-sm"
            >
              <option value="pickup">In arrivo per il ritiro</option>
              <option value="delivery">In arrivo per la consegna</option>
            </select>
          </label>
        )}
      </div>

      <button
        onClick={onSend}
        disabled={sending}
        data-testid="admin-push-sim-send"
        className="mt-4 h-11 px-5 rounded-xl bg-dapnow-blue text-white text-sm font-semibold hover:bg-dapnow-blue-hover transition disabled:opacity-60 flex items-center gap-1.5"
      >
        <Send className="size-4" /> {sending ? "Invio…" : "Triggera evento"}
      </button>

      {lastResult && (
        <pre className="mt-4 text-xs bg-dapnow-bg border border-dapnow-line rounded-xl p-3 overflow-x-auto">
{JSON.stringify(lastResult, null, 2)}
        </pre>
      )}
    </div>
  );
}
